import { AppState } from '../reducers';

// services
import * as Services from '../../services/ProviderConsumptionImport.Services';
import * as ProtocolServices from '../../services/MedicalProtocols.Services';
import * as ProviderServices from '../../services/Provider.Services';

// functions
import { findProviderRouteId } from '../../functions/ProviderConsumptionImport.Functions';

// models
import { ProviderFileViewModel } from '../../models/Entities/AzureFiles/AzureFileUpload';
import {
  DeleteImportedConsumptionsResult,
  GenerateProtocolConsumptionsResult,
  ImportedConsumptionsFilter,
  ImportedConsumptionsPage,
  ImportedProviderConsumption,
  ImportPreview,
  ImportProviderFileConsumptionsRequest,
  ImportStartResponse,
  PreviewItemIds,
  PreviewItemsFilter,
  PreviewItemsPage,
  PreviewItemsQuery,
  ProviderConsumptionAssignmentRequest,
  ProtocolReferenceTemplate,
  ProviderMedicalServiceMapping,
  SaveReferenceTemplateRequest,
  ServiceMappingsPage,
  ServiceMappingsQuery,
  TemplateTestRequest,
  TemplateTestResult,
} from '../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { Protocol } from '../../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../../models/Entities/Protocols/ProtocolPatient';
import { ErrorType } from '../../models/HttpError';

/**
 * Importación de consumos de proveedor (spec 005). El token se lee sólo acá; los
 * servicios lo reciben por parámetro. Los thunks devuelven promesas (sin estado en
 * el store): cada pantalla guarda el resultado en su estado local y muestra los
 * errores (ErrorType con los `Errors[]` de la API).
 */

export type ProviderConsumptionImportThunk<TResult> = (dispatch: (action: unknown) => void, getState: () => AppState) => Promise<TResult>;

const NotAuthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] };

export const toErrorType = (error: unknown): ErrorType => {
  if (typeof error === 'object' && error !== null && 'ErrorCode' in error) return error as ErrorType;
  return { ErrorCode: 0, ErrorMessage: error instanceof Error ? error.message : 'Unexpected error', Errors: [] };
};

const requireToken = (getState: () => AppState): string => {
  const token = getState().oidc?.user?.access_token;
  if (!token) throw NotAuthorized;
  return token;
};

const run = async <T>(getState: () => AppState, call: (token: string) => Promise<T>): Promise<T> => {
  const token = requireToken(getState);
  try {
    return await call(token);
  } catch (error: unknown) {
    throw toErrorType(error);
  }
};

/** Proveedor de origen de un consumo de protocolo generado desde un consumo importado (spec 005, US8). */
export interface SourceProviderInfo {
  /** Provider.id (int) para la ruta /providers/{id}; undefined si no se pudo resolver */
  providerId?: number;
  /** person.personIdentifier (Guid) */
  providerIdentifier?: string;
  providerName?: string;
}

/** Cuántos proveedores se piden al buscar por nombre para resolver el id de la ruta. */
export const SOURCE_PROVIDER_SEARCH_PAGE_SIZE = 50;

export const actionCreators = {
  /**
   * Resuelve el proveedor de origen de un consumo de protocolo con `sourceProviderConsumptionId`: usa el Guid y el
   * nombre del proveedor del consumo de protocolo y, si faltan, lee el consumo de proveedor
   * (GET protocols/providers/consumptions/{id}). El id int de la ruta sale de la lista de proveedores del store o,
   * si no está, de buscar por nombre (GET providers?searchText=) y comparar el Guid. Rechaza con ErrorType.
   */
  ResolveSourceProvider:
    (
      sourceProviderConsumptionId: string,
      providerIdentifier?: string | null,
      providerName?: string | null
    ): ProviderConsumptionImportThunk<SourceProviderInfo> =>
    async (_dispatch, getState) =>
      run(getState, async (token) => {
        let identifier = providerIdentifier?.trim() || undefined;
        let name = providerName?.trim() || undefined;
        if (!identifier) {
          const source = await Services.GetProviderConsumption(token, sourceProviderConsumptionId);
          identifier = source?.providerId?.trim() || undefined;
          name = name ?? (source?.providerName?.trim() || undefined);
        }
        if (!identifier) return { providerName: name };
        let providerId = findProviderRouteId(getState().Provider?.list, identifier);
        if (providerId === undefined && name) {
          const found = await ProviderServices.GetProviders(1, SOURCE_PROVIDER_SEARCH_PAGE_SIZE, name, token);
          providerId = findProviderRouteId(found, identifier);
        }
        return { providerId, providerIdentifier: identifier, providerName: name };
      }),

  /** Archivos del proveedor (ProviderId int); el front filtra los importables. */
  LoadImportableProviderFiles:
    (providerId: number): ProviderConsumptionImportThunk<ProviderFileViewModel[]> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetImportableProviderFiles(token, providerId)),

  /** Vista previa del mapeo de un archivo (no escribe nada). */
  PreviewFileImport:
    (providerId: number, fileId: string | number, body: ImportProviderFileConsumptionsRequest = {}): ProviderConsumptionImportThunk<ImportPreview | undefined> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.PreviewProviderFileImport(token, providerId, fileId, body)),

  /** Importa los consumos de un archivo con el mismo cuerpo de la vista previa: 202 + job (MP 030) o resumen (anterior); 409 con JobId = ya hay una en curso. */
  ImportFileConsumptions:
    (providerId: number, fileId: string | number, body: ImportProviderFileConsumptionsRequest = {}): ProviderConsumptionImportThunk<ImportStartResponse> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.ImportProviderFileConsumptions(token, providerId, fileId, body)),

  /** Página de ítems de la vista previa congelada (spec 009, MP 031); 404 = vencida o reemplazada. */
  LoadPreviewItems:
    (providerId: number, previewId: string, query: PreviewItemsQuery): ProviderConsumptionImportThunk<PreviewItemsPage> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetPreviewItems(token, providerId, previewId, query)),

  /** Ids e importes de los ítems seleccionables que cumplen los filtros (selección inicial, "Todos los filtrados"). */
  LoadPreviewItemIds:
    (providerId: number, previewId: string, filter: PreviewItemsFilter = {}): ProviderConsumptionImportThunk<PreviewItemIds> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetPreviewItemIds(token, providerId, previewId, filter)),

  /** Correspondencias de servicio del proveedor, paginadas y con búsqueda (spec 009, US6). */
  LoadServiceMappings:
    (providerId: number, filter: ServiceMappingsQuery): ProviderConsumptionImportThunk<ServiceMappingsPage> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetServiceMappings(token, providerId, filter)),

  /** Cambia el servicio médico de una correspondencia (400 si el servicio no existe o está inactivo). */
  UpdateServiceMapping:
    (providerId: number, mappingId: string, medicalServiceId: number): ProviderConsumptionImportThunk<ProviderMedicalServiceMapping | undefined> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.UpdateServiceMapping(token, providerId, mappingId, medicalServiceId)),

  /** Elimina una correspondencia (borrado físico). */
  DeleteServiceMapping:
    (providerId: number, mappingId: string): ProviderConsumptionImportThunk<void> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.DeleteServiceMapping(token, providerId, mappingId)),

  /** Formatos de referencia del proveedor (activos e inactivos, en orden; spec 009, US5). */
  LoadReferenceTemplates:
    (providerId: number): ProviderConsumptionImportThunk<ProtocolReferenceTemplate[]> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetReferenceTemplates(token, providerId)),

  /** Alta de un formato (queda al final; 409 con 20 activos). */
  CreateReferenceTemplate:
    (providerId: number, body: SaveReferenceTemplateRequest): ProviderConsumptionImportThunk<ProtocolReferenceTemplate | undefined> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.CreateReferenceTemplate(token, providerId, body)),

  /** Edición de un formato (nombre, partes, texto de ejemplo y activo). */
  UpdateReferenceTemplate:
    (providerId: number, templateId: string, body: SaveReferenceTemplateRequest): ProviderConsumptionImportThunk<ProtocolReferenceTemplate | undefined> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.UpdateReferenceTemplate(token, providerId, templateId, body)),

  /** Elimina un formato (borrado lógico). */
  DeleteReferenceTemplate:
    (providerId: number, templateId: string): ProviderConsumptionImportThunk<void> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.DeleteReferenceTemplate(token, providerId, templateId)),

  /** Nuevo orden de aplicación (todas las plantillas del proveedor). */
  ReorderReferenceTemplates:
    (providerId: number, templateIds: string[]): ProviderConsumptionImportThunk<ProtocolReferenceTemplate[]> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.ReorderReferenceTemplates(token, providerId, templateIds)),

  /** Prueba un formato con hasta 500 textos (no guarda). */
  TestReferenceTemplate:
    (providerId: number, body: TemplateTestRequest): ProviderConsumptionImportThunk<TemplateTestResult[]> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.TestReferenceTemplate(token, providerId, body)),

  /** Elimina todos los consumos importados de un archivo (todo o nada; 409 si alguno ya se generó o no está pendiente). */
  DeleteImportedConsumptionsOfFile:
    (providerId: number, fileId: string | number): ProviderConsumptionImportThunk<DeleteImportedConsumptionsResult> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.DeleteImportedConsumptionsOfFile(token, providerId, fileId)),

  /** Consumos importados del proveedor (ProviderId Guid). */
  LoadImportedConsumptions:
    (providerIdentifier: string, filter: ImportedConsumptionsFilter): ProviderConsumptionImportThunk<ImportedConsumptionsPage> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetImportedConsumptions(token, providerIdentifier, filter)),

  /** Asigna protocolo y paciente a los consumos; devuelve los consumos actualizados (concepto o alerta). */
  AssignProtocolAndPatient:
    (request: ProviderConsumptionAssignmentRequest): ProviderConsumptionImportThunk<ImportedProviderConsumption[]> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.AssignProtocolAndPatient(token, request)),

  /** Genera los consumos de protocolo de los consumos asignados. */
  GenerateProtocolConsumptions:
    (ids: string[]): ProviderConsumptionImportThunk<GenerateProtocolConsumptionsResult> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GenerateProtocolConsumptions(token, ids)),

  /** Protocolos para el selector "id - nombre". */
  LoadProtocolsForAssignment: (): ProviderConsumptionImportThunk<Protocol[]> => async (_dispatch, getState) =>
    run(getState, async (token) => (await ProtocolServices.GetAllProtocols(token)) ?? []),

  /** Pacientes del protocolo (protocols/{id}/patients). */
  LoadProtocolPatients:
    (protocolId: number): ProviderConsumptionImportThunk<ProtocolPatient[]> =>
    async (_dispatch, getState) =>
      run(getState, async (token) => (await ProtocolServices.GetProtocolPatients(protocolId, null, token)) ?? []),
};
